import React, { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import Header from './components/Header'
import Footer from './components/Footer'
import CookieBanner from './components/CookieBanner'
import AuthErrorRecovery from './components/AuthErrorRecovery'
import { trackPageView } from './lib/analytics'

// Import pages
import Home from './pages/Home'
import Pricing from './pages/Pricing'
import SchoolPurchase from './pages/SchoolPurchase'
import Auth from './pages/Auth'
import Dashboard from './pages/Dashboard'
import TestPage from './pages/TestPage'
import Results from './pages/Results'
import PaymentSuccess from './pages/PaymentSuccess'
import MyResults from './pages/MyResults'
import AccountSettings from './pages/AccountSettings'
import Feedback from './pages/Feedback'
import SampleTest from './pages/SampleTest'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import FinancialLiteracy from './pages/FinancialLiteracy'
import SchoolBuffetScenario from './pages/scenarios/SchoolBuffetScenario'
import MobileScenario from './pages/scenarios/MobileScenario'
import MoneyConceptScenario from './pages/scenarios/MoneyConceptScenario'
import CashOrCardScenario from './pages/scenarios/CashOrCardScenario'
import SurviveMonthScenario from './pages/scenarios/SurviveMonthScenario'
import BorrowOrWaitScenario from './pages/scenarios/BorrowOrWaitScenario'
import SavingOrInvestingScenario from './pages/scenarios/SavingOrInvestingScenario'
import SuspiciousBankMessageScenario from './pages/scenarios/SuspiciousBankMessageScenario'
import NeedsWantsScenario from './pages/scenarios/NeedsWantsScenario'
import GradeSelection from './pages/scenarios/GradeSelection'
import GradeScenarios from './pages/scenarios/GradeScenarios'

// Legal & Contact pages
import Contact from './pages/Contact'
import PrivacyPolicy from './pages/PrivacyPolicy'
import TermsOfUse from './pages/TermsOfUse'

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard'
import AdminTests from './pages/admin/AdminTests'
import AdminTestForm from './pages/admin/AdminTestForm'
import AdminQuestions from './pages/admin/AdminQuestions'
import AdminFinancialScenarios from './pages/admin/AdminFinancialScenarios'
import AdminCustomOffers from './pages/admin/AdminCustomOffers'
import AdminPayments from './pages/admin/AdminPayments'
import AdminUsers from './pages/admin/AdminUsers'
import AdminAdmins from './pages/admin/AdminAdmins'

// School Custom Offer page
import SchoolOfferPage from './pages/SchoolOfferPage'
import IndividualOfferPage from './pages/IndividualOfferPage'

import './App.css'

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, authError } = useAuth()

  // Auth error detected - show recovery screen
  if (authError) {
    return <AuthErrorRecovery />
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return user ? children : <Navigate to="/login" />
}

// Public Only Route (redirect to dashboard if already logged in)
const PublicOnlyRoute = ({ children }) => {
  const { user, loading, authError } = useAuth()

  // Auth error detected - show recovery screen
  if (authError) {
    return <AuthErrorRecovery />
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return !user ? children : <Navigate to="/dashboard" />
}

// Admin Route (only for admin users)
const AdminRoute = ({ children }) => {
  const { user, loading, authError, isAdmin, adminChecked } = useAuth()
  const [isReady, setIsReady] = useState(false)

  console.log('[AdminRoute] RENDER:', { user: !!user, loading, isAdmin, adminChecked, isReady })

  // Wait for admin check to complete AND isAdmin value to stabilize
  useEffect(() => {
    if (adminChecked && isAdmin) {
      // Admin confirmed - mark as ready immediately
      setIsReady(true)
    } else if (adminChecked && !isAdmin) {
      // Not admin - wait a bit to ensure isAdmin isn't about to change
      const timer = setTimeout(() => {
        setIsReady(true)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [adminChecked, isAdmin])

  // Auth error detected - show recovery screen
  if (authError) {
    return <AuthErrorRecovery />
  }

  // Wait for auth and admin check to fully complete
  if (loading || !adminChecked || !isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Kontrolujem oprávnenia...</p>
        </div>
      </div>
    )
  }

  // Not logged in -> login page
  if (!user) {
    return <Navigate to="/login" />
  }

  // Not admin -> dashboard
  if (!isAdmin) {
    console.log('[AdminRoute] ❌ Non-admin user, redirecting to dashboard. isAdmin=', isAdmin)
    return <Navigate to="/dashboard" replace />
  }

  // Admin user - show content
  console.log('[AdminRoute] ✅ Admin user authenticated')
  return children
}

function AppRoutes() {
  const { user, authError } = useAuth()
  
  // Top-level auth error check - show recovery screen
  if (authError) {
    return <AuthErrorRecovery />
  }
  
  return (
    <>
      <Header />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/school-purchase" element={<SchoolPurchase />} />
        <Route path="/school-offer/:token" element={<SchoolOfferPage />} />
        <Route path="/individual-offer/:token" element={<IndividualOfferPage />} />
        <Route path="/financial-literacy" element={<FinancialLiteracy />} />
        <Route path="/scenarios" element={<GradeSelection />} />
        <Route path="/scenarios/school-buffet" element={<SchoolBuffetScenario />} />
        <Route path="/scenarios/mobile" element={<MobileScenario />} />
        <Route path="/scenarios/money-concept" element={<MoneyConceptScenario />} />
        <Route path="/scenarios/cash-or-card" element={<CashOrCardScenario />} />
        <Route path="/scenarios/survive-month" element={<SurviveMonthScenario />} />
        <Route path="/scenarios/borrow-or-wait" element={<BorrowOrWaitScenario />} />
        <Route path="/scenarios/saving-or-investing" element={<SavingOrInvestingScenario />} />
        <Route path="/scenarios/suspicious-bank-message" element={<SuspiciousBankMessageScenario />} />
        <Route path="/scenarios/needs-wants" element={<NeedsWantsScenario />} />
        <Route path="/scenarios/:grade" element={<GradeScenarios />} />
        
        {/* Auth Routes (redirect if already logged in) */}
        <Route 
          path="/login" 
          element={
            <PublicOnlyRoute>
              <Auth />
            </PublicOnlyRoute>
          } 
        />
        <Route 
          path="/register" 
          element={
            <PublicOnlyRoute>
              <Auth />
            </PublicOnlyRoute>
          } 
        />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/test/:testId"
          element={
            <ProtectedRoute>
              <TestPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/results/:attemptId"
          element={
            <ProtectedRoute>
              <Results />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-success"
          element={
            <ProtectedRoute>
              <PaymentSuccess />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-results"
          element={
            <ProtectedRoute>
              <MyResults />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <AccountSettings />
            </ProtectedRoute>
          }
        />
        
        {/* Feedback Route - accessible to everyone */}
        <Route path="/feedback" element={<Feedback />} />

        {/* Public Sample Test (no auth) */}
        <Route path="/sample-test/:testId" element={<SampleTest />} />
        
        {/* Password Reset Routes - accessible to everyone */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Legal & Contact pages (public) */}
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-use" element={<TermsOfUse />} />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/tests"
          element={
            <AdminRoute>
              <AdminTests />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/tests/new"
          element={
            <AdminRoute>
              <AdminTestForm />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/tests/:testId/edit"
          element={
            <AdminRoute>
              <AdminTestForm />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/tests/:testId/questions"
          element={
            <AdminRoute>
              <AdminQuestions />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/financial-scenarios"
          element={
            <AdminRoute>
              <AdminFinancialScenarios />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/custom-offers"
          element={
            <AdminRoute>
              <AdminCustomOffers />
            </AdminRoute>
          }
        />

        {/* Admin Payments Route */}
        <Route
          path="/admin/payments"
          element={
            <AdminRoute>
              <AdminPayments />
            </AdminRoute>
          }
        />

        {/* Admin Users Overview (read-only) */}
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <AdminUsers />
            </AdminRoute>
          }
        />

        {/* Phase 3 — Admin team management (owner-only inside the page) */}
        <Route
          path="/admin/admins"
          element={
            <AdminRoute>
              <AdminAdmins />
            </AdminRoute>
          }
        />


        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      <Footer />
      <CookieBanner />
    </>
  )
}

// Fires a GA4 page_view on every SPA route change (silently no-ops until
// analytics consent is granted).
function AnalyticsRouteListener() {
  const location = useLocation()
  useEffect(() => {
    trackPageView(location.pathname + location.search, document.title)
  }, [location.pathname, location.search])
  return null
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AnalyticsRouteListener />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  )
}

export default App
